import { Badge, Group, Stack, Stepper, Text } from "@mantine/core";
import {
  ConfigurableProduct,
  useProductWorkflow,
  WorkflowStepState,
} from "@jasper/product-workflows";
import { getStepsForProduct } from "@jasper/product-workflows";
import { StepKeys } from "@jasper/product-workflows";
import { IconCheck, IconLock } from "@tabler/icons-react";

interface Props {
  orderId: string;
  productId: string;
  steps: ReturnType<typeof getStepsForProduct>;
  completedStepKeys: StepKeys[];
  bulkProductIds?: string[];
  product: ConfigurableProduct;
  activeStep?: StepKeys;
}

const isCompleted = (step: WorkflowStepState<StepKeys>) =>
  step.reachable && (step.completed || !!step.optional);

const ProductStepper = ({
  orderId,
  productId,
  steps,
  completedStepKeys,
  bulkProductIds,
  product,
  activeStep,
}: Props) => {
  const { goTo, stepIndex, activeSteps } = useProductWorkflow({
    steps,
    orderId,
    productId,
    completedStepKeys,
    onComplete: () => undefined,
    bulkProductIds,
    product,
  });

  const activeStepIndex = activeStep
    ? activeSteps.findIndex(step => step.key === activeStep)
    : stepIndex;

  // We want to show a step as active only if it is reachable. If the active step is not reachable, we will show the last reachable step as active.
  const lastReachableStepIndex = activeSteps.reduce(
    (lastIndex, step, index) => (step.reachable ? index : lastIndex),
    -1
  );

  const finalActiveStepIndex = Math.max(
    0,
    Math.min(activeStepIndex, lastReachableStepIndex)
  );

  const getIconForStep = (step: WorkflowStepState<StepKeys>, index: number) => {
    if (!step.reachable) return <IconLock size={14} />;
    return index !== finalActiveStepIndex && isCompleted(step) ? (
      <IconCheck size={14} />
    ) : undefined;
  };

  return (
    <Stack>
      <Group justify="space-between">
        <Text variant="overline">Étapes</Text>
        <Badge leftSection={null}>
          <Text span variant="overline" c="brand.7">
            Étape {finalActiveStepIndex + 1} / {activeSteps.length}
          </Text>
        </Badge>
      </Group>
      <Stepper
        active={finalActiveStepIndex}
        orientation="vertical"
        iconSize={28}
        variant="flow"
        onStepClick={clickedIndex => {
          const target = activeSteps[clickedIndex];
          goTo(target.key);
        }}
      >
        {activeSteps.map((step, index) => (
          <Stepper.Step
            key={step.key}
            label={<span data-text={step.label}>{step.label}</span>}
            description={step.optional ? "optionnel" : undefined}
            mod={{ done: isCompleted(step) }}
            disabled={!step.reachable}
            allowStepSelect={step.reachable}
            icon={getIconForStep(step, index)}
          />
        ))}
      </Stepper>
    </Stack>
  );
};

export default ProductStepper;
